import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { ScoreOverview, ScoresEveryDay, ScoresEveryDayBrand } from "../../types/scores";
import type {
  ScoresState,
  Scores,
  OverviewTopModel,
  Brand7dSummary,
} from "./types";
import type { 
  FetchDailyAverageModelPayload, 
  FetchOverviewScoresFromDailyPayload, 
  FetchOverviewTopModelPayload, 
  FetchScoresPromptPayload, 
  FetchScoresModelPayload, 
  fetchBrandSeriesPayload,
  FetchBrandByPromptsSeriesPayload,
} from "./payload.types";



export const initialState: ScoresState = {
  brand7dSummary: {
    loading: "idle",
    data: null,
  },
  dailyAverageBrand: {
    loading: "idle",
    data: null,
    rangeValues: null,
  },
  dailyAveragePrompt: {
    loading: "idle",
    data: null,
    rangeValues: null,
    error: null,
  },
  dailyAverageModel: {
    loading: "idle",
    data: null,
    rangeValues: null,
    error: null,
  },

  scoresByPrompts: {
    loading: false,
    data: null,
  },
  scoresByModels: {
    loading: false,
    data: null,
  },

  lastUpdates: {
    loading: false,
    data: null,
  },

  overviewScoresFromDaily: {
    loading: "idle",
    data: null,
  },

  overviewTopModel: {
    loading: "idle",
    data: null,
  },
};


const scoresSlice = createSlice({
  name: "scores",
  initialState,
  reducers: {
    fetchBrand7dSummary: (state) => {
      state.brand7dSummary.loading = "loading";
    },
    fetchBrand7dSummarySuccess: (
      state,
      action: PayloadAction<Record<number, Brand7dSummary>>,
    ) => {
      state.brand7dSummary.loading = "success";
      state.brand7dSummary.data = action.payload;
    },
    fetchBrand7dSummaryFailure: (state) => {
      state.brand7dSummary.loading = "error";
    },
    fetchBrandSeries: (state, action: PayloadAction<fetchBrandSeriesPayload>) => {
      void action.payload;
      state.dailyAverageBrand.loading = "loading";
      state.dailyAverageBrand.data = null;
      state.dailyAverageBrand.rangeValues = null;
    },
    fetchBrandSeriesSuccess: (state, action: PayloadAction<ScoresEveryDayBrand>) => {
      state.dailyAverageBrand.loading = "success";
      state.dailyAverageBrand.data = action.payload;
    },
    fetchBrandSeriesFailure: (state) => {
      state.dailyAverageBrand.loading = "error";
    },
    fetchBrandByPromptsSeries: (state, action: PayloadAction<FetchBrandByPromptsSeriesPayload>) => {
      void action.payload;
      state.dailyAveragePrompt.loading = "loading";
      state.dailyAveragePrompt.data = null;
      state.dailyAveragePrompt.rangeValues = null;
      state.dailyAveragePrompt.error = null;
    },
    fetchBrandByPromptsSeriesSuccess: (state, action: PayloadAction<ScoresEveryDay>) => {
      state.dailyAveragePrompt.loading = "success";
      state.dailyAveragePrompt.data = action.payload;
    },
    fetchBrandByPromptsSeriesFailure: (state) => {
      state.dailyAveragePrompt.loading = "error";
    },
    resetDailyAveragePrompt: (state) => {
      state.dailyAveragePrompt.data = null;
      state.dailyAveragePrompt.rangeValues = null;
      state.dailyAveragePrompt.loading = "idle";
      state.dailyAveragePrompt.error = null;
    },




    

    setLoadingDailyAverageModel: (state) => {
      state.dailyAverageModel.loading = "loading";
      state.dailyAverageModel.error = null;
    },
    fetchDailyAverageModelSuccess: (state, action) => {
      state.dailyAverageModel.loading = "success";
      state.dailyAverageModel.data = action.payload;
      state.dailyAverageModel.error = null;
    },
    fetchDailyAverageModelFailure: (state, action: PayloadAction<string | undefined>) => {
      state.dailyAverageModel.loading = "error";
      state.dailyAverageModel.data = null;
      state.dailyAverageModel.error = action.payload ?? null;
    },
    resetDailyAverageModel: (state) => {
      state.dailyAverageModel.data = null;
      state.dailyAverageModel.rangeValues = null;
      state.dailyAverageModel.loading = "idle";
      state.dailyAverageModel.error = null;
    },
    fetchLastUpdatesLoading: (state) => {
      state.lastUpdates.loading = true;
    },
    fetchLastUpdatesBrandsSuccess: (state, action) => {
      state.lastUpdates.loading = false;
      state.lastUpdates.data = {
        ...state.lastUpdates.data,
        ...action.payload,
      };
    },
    fetchLastUpdatesBrandsFailure: (state) => {
      state.lastUpdates.loading = false;
    },

    fetchScorePromptSuccess: (state, action: PayloadAction<{ prompt_id: number | string; scores: Scores | null }>) => {
      state.scoresByPrompts.loading = false;
      
      const { prompt_id, scores } = action.payload;
      
      if (!state.scoresByPrompts.data) {
        state.scoresByPrompts.data = {};
      }
      
      if (scores && typeof scores === 'object' && scores !== null) {
        const promptIdKey = String(prompt_id);
        const promptData = scores[promptIdKey];
        if (promptData) {
          state.scoresByPrompts.data[promptIdKey] = promptData;
        }
      } else if (scores === null) {
        state.scoresByPrompts.data[String(prompt_id)] = {};
      }
    },
    fetchScorePromptFailure: (state) => {
      state.scoresByPrompts.loading = false;
    },

    fetchScoreModelSuccess: (state, action: PayloadAction<{ model_id: number | string; scores: Scores | null }>) => {
      state.scoresByModels.loading = false;
      
      const { model_id, scores } = action.payload;
      
      if (!state.scoresByModels.data) {
        state.scoresByModels.data = {};
      }
      
      if (scores && typeof scores === 'object' && scores !== null) {
        const modelIdKey = String(model_id);
        const modelData = scores[modelIdKey];
        if (modelData) {
          state.scoresByModels.data[modelIdKey] = modelData;
        }
      } else if (scores === null) {
        state.scoresByModels.data[String(model_id)] = {};
      }
    },
    fetchScoreModelFailure: (state) => {
      state.scoresByModels.loading = false;
    },

    clearScoreByPrompts: (state) => {
      state.scoresByPrompts.data = null;
    },
    clearScoreByModels: (state) => {
      state.scoresByModels.data = null;
    },

    setDailyAverageBrandRangeValues: (state, action) => {
      state.dailyAverageBrand.rangeValues = action.payload;
    },

    setDailyAveragePromptRangeValues: (state, action) => {
      state.dailyAveragePrompt.rangeValues = action.payload;
    },
    setDailyAverageModelRangeValues: (state, action) => {
      state.dailyAverageModel.rangeValues = action.payload;
    },

    fetchOverviewScoresFromDaily: (state) => {
      state.overviewScoresFromDaily.loading = "loading";
      state.overviewScoresFromDaily.data = null;
    },
    fetchOverviewScoresFromDailySuccess: (state, action: PayloadAction<ScoreOverview[]>) => {
      state.overviewScoresFromDaily.loading = "success";
      state.overviewScoresFromDaily.data = action.payload;
    },
    fetchOverviewScoresFromDailyFailure: (state) => {
      state.overviewScoresFromDaily.loading = "error";
    },

    fetchOverviewTopModel: (state) => {
      state.overviewTopModel.loading = "loading";
      state.overviewTopModel.data = null;
    },
    fetchOverviewTopModelSuccess: (state, action: PayloadAction<OverviewTopModel | null>) => {
      state.overviewTopModel.loading = "success";
      state.overviewTopModel.data = action.payload;
    },
    fetchOverviewTopModelFailure: (state) => {
      state.overviewTopModel.loading = "error";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchScorePrompt, (state, action) => {
        const promptId = String(action.payload.prompt_id);
        if (state.scoresByPrompts.data != null) {
          delete state.scoresByPrompts.data[promptId];
        }
      })
      .addCase(fetchScoreModel, (state, action) => {
        const modelId = String(action.payload.model_id);
        if (state.scoresByModels.data != null) {
          delete state.scoresByModels.data[modelId];
        }
      });
  },
});



export const fetchLastUpdatesBrand = createAction<number>("scores/fetchLastUpdatesBrand");
export const fetchDailyAverageModel = createAction<FetchDailyAverageModelPayload>("scores/fetchDailyAverageModel");
export const fetchOverviewScoresFromDaily = createAction<FetchOverviewScoresFromDailyPayload>("scores/fetchOverviewScoresFromDaily");
export const fetchOverviewTopModel = createAction<FetchOverviewTopModelPayload>("scores/fetchOverviewTopModel");
export const updateScoresWS = createAction("scores/updateScoresWS");
export const fetchScorePrompt = createAction<FetchScoresPromptPayload>("scores/fetchScorePrompt");
export const fetchScoreModel = createAction<FetchScoresModelPayload>("scores/fetchScoreModel");


export const {
  fetchBrand7dSummary,
  fetchBrand7dSummarySuccess,
  fetchBrand7dSummaryFailure,
  fetchBrandSeries,
  fetchBrandSeriesSuccess,
  fetchBrandSeriesFailure,
  fetchBrandByPromptsSeries,
  fetchBrandByPromptsSeriesSuccess,
  fetchBrandByPromptsSeriesFailure,
  resetDailyAveragePrompt,
  setLoadingDailyAverageModel,
  fetchDailyAverageModelSuccess,
  fetchDailyAverageModelFailure,
  resetDailyAverageModel,
  fetchLastUpdatesLoading,
  fetchLastUpdatesBrandsSuccess,
  fetchLastUpdatesBrandsFailure,
  fetchScorePromptSuccess,
  fetchScoreModelSuccess,
  clearScoreByPrompts,
  clearScoreByModels,
  fetchScorePromptFailure,
  fetchScoreModelFailure,
  setDailyAverageBrandRangeValues,
  setDailyAveragePromptRangeValues,
  setDailyAverageModelRangeValues,
  fetchOverviewScoresFromDailySuccess,
  fetchOverviewScoresFromDailyFailure,
  fetchOverviewTopModelSuccess,
  fetchOverviewTopModelFailure,
} = scoresSlice.actions;

export default scoresSlice.reducer;
